/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment, useState } from 'react';
import { Divider, Grid, Typography } from '@material-ui/core';
import { RouteComponentProps, navigate, useLocation } from '@reach/router';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import Backdrop from '../../../../backdrop/Backdrop';
import theme from '../../../../../themes/theme';
import colors from '../../../../../themes/main-colors';
import { Link } from 'gatsby';
import Buttons from '../../../../buttons';
import { PgeEvModel } from '../../../../pge-plus-common/PgeEvModel';
import { getFormattedCurrencyString } from '../../../../../util/format';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import { getSmartChargingRebate } from '../rebateOnlyForm.utils';

type TEligibilityConfirmationState = {
  /***
   * Pass the slug as string
   * Entry has to be added and formatted in the PGE+ Eligibility confirmation microcopy group.
   ***/
  message: string;
  backButtonRoute: string;
  nextButtonRoute: string;
};

const EligibilityConfirmation: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content, richText, isContentLoading } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION,
  );
  const [isPGEEvModelVisible, setIsPGEEvModelVisible] = useState<boolean>(
    false,
  );
  const location = useLocation();
  const {
    message,
    backButtonRoute,
    nextButtonRoute,
  } = location.state as TEligibilityConfirmationState;
  const {
    formState: { rebateConfirm },
  } = useRebateOnlyFormState();
  const rebateSmartCharging = getSmartChargingRebate(
    rebateConfirm.preRebateEligibility2,
    rebateConfirm.preRebateEligibility1,
  );
  const chargerAmount =
    rebateSmartCharging?.charger ?? rebateSmartCharging?.amount;
  const panelUpgradeAmount = rebateSmartCharging?.panelUpgrade;
  const rebateAmounts = {
    CHARGER_AMOUNT: getFormattedCurrencyString(chargerAmount || ''),
    PANEL_UPGRADE: getFormattedCurrencyString(panelUpgradeAmount || ''),
  };

  if (isContentLoading) {
    return <Backdrop forceOpen />;
  }

  return (
    <Grid container>
      <Grid item>
        {message === 'REBATE_ONLY_REBATE_RESULTS_AFTER_QPL_STANDARD' ? (
          <Fragment>
            <CustomTypography>
              {richText(
                content.get('REBATE_ONLY_REBATE_RESULTS_AFTER_QPL_STANDARD_1'),
                rebateAmounts,
              )}
            </CustomTypography>
          </Fragment>
        ) : message === 'EV_PULSE_REBATE_OFFER_DETAILS' ? (
          <Fragment>
            <CustomTypography>
              {richText(content.get('EV_PULSE_REBATE_OFFER_DETAILS_1'))}
            </CustomTypography>
          </Fragment>
        ) : (
          <CustomTypography>
            {richText(content.get(message), rebateAmounts)}
          </CustomTypography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />
      </Grid>
      <Grid
        item
        container
        spacing={2}
        css={{
          marginTop: 24,
          marginBottom: 24,
          justifyContent: 'center',
        }}
      >
        <Grid item xs={6} md={3}>
          <Link to={backButtonRoute}>
            <Buttons
              type="button"
              fullWidth
              variant="outlined"
              color="secondary"
              css={{
                fontSize: '18px',
                borderRadius: '30px',
                '.MuiButton-label': {
                  fontWeight: 500,
                },
                '&:hover': {
                  borderWidth: '2px',
                },
              }}
            >
              {content.get('REUSABLE_BUTTON_BACK')}
            </Buttons>
          </Link>
        </Grid>
        <Grid item xs={6} md={3}>
          <Link to={nextButtonRoute}>
            <Buttons
              type="button"
              fullWidth
              variant="contained"
              css={{
                fontSize: '18px',
                borderRadius: '30px',
                '.MuiButton-label': {
                  fontWeight: 500,
                },
              }}
            >
              {content.get('REUSABLE_BUTTON_NEXT')}
            </Buttons>
          </Link>
        </Grid>
      </Grid>
      <PgeEvModel
        isVisible={isPGEEvModelVisible}
        setIsVisible={setIsPGEEvModelVisible}
      />
    </Grid>
  );
};

const CustomTypography = ({ children }: { children: React.ReactNode }) => {
  return (
    <Typography
      variant="body1"
      css={{
        fontSize: 20,
        [theme.breakpoints.down('xs')]: {
          fontSize: 16,
        },
        '& a': { textDecoration: 'none' },
      }}
    >
      {children}
    </Typography>
  );
};

export default EligibilityConfirmation;
